<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ReportHeader from './components/ReportHeader.vue';

const { t } = useI18n();
const store = useStore();

const stats = ref([]);
const isLoading = ref(false);
const isResetting = ref(false);

const fetchStats = async () => {
  isLoading.value = true;
  try {
    const data = await store.dispatch('agents/fetchTourStats');
    stats.value = data.tour_stats || [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchStats);

const summary = computed(() => {
  const total = stats.value.length;
  const completed = stats.value.filter(s => s.status === 'completed').length;
  const skipped = stats.value.filter(s => s.status === 'skipped').length;
  const pending = stats.value.filter(s => s.status === 'pending').length;
  const skipRate =
    total > 0 ? Math.round(((skipped + pending) / total) * 100) : 0;
  return { total, completed, skipped, pending, skipRate };
});

const resetUser = async user => {
  isResetting.value = true;
  try {
    await store.dispatch('agents/resetTour', [user.id]);
    await fetchStats();
  } finally {
    isResetting.value = false;
  }
};

const statusLabel = status => t(`TOUR_REPORTS.STATUS.${status.toUpperCase()}`);
const statusClass = status => {
  if (status === 'completed')
    return 'bg-g-50 text-g-700 dark:bg-g-900 dark:text-g-300';
  if (status === 'skipped')
    return 'bg-y-50 text-y-700 dark:bg-y-900 dark:text-y-300';
  return 'bg-n-alpha-2 text-n-slate-11';
};
</script>

<template>
  <ReportHeader :header-title="$t('TOUR_REPORTS.HEADER')" />

  <div class="flex flex-col gap-6 pb-6">
    <!-- Summary cards -->
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div
        class="flex flex-col gap-1 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('TOUR_REPORTS.SUMMARY.TOTAL')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          summary.total
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('TOUR_REPORTS.SUMMARY.COMPLETED')
        }}</span>
        <span class="text-2xl font-semibold text-g-600">{{
          summary.completed
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('TOUR_REPORTS.SUMMARY.SKIPPED')
        }}</span>
        <span class="text-2xl font-semibold text-y-600">{{
          summary.skipped
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('TOUR_REPORTS.SUMMARY.SKIP_RATE')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12"
          >{{ summary.skipRate }}%</span
        >
      </div>
    </div>

    <!-- Table -->
    <div class="rounded-xl border border-n-weak overflow-hidden">
      <div v-if="isLoading" class="py-12 text-center text-n-slate-11 text-sm">
        {{ $t('TOUR_REPORTS.LOADING') }}
      </div>
      <div
        v-else-if="stats.length === 0"
        class="py-12 text-center text-n-slate-11 text-sm"
      >
        {{ $t('TOUR_REPORTS.EMPTY') }}
      </div>
      <table v-else class="w-full text-sm">
        <thead>
          <tr class="bg-n-alpha-1 border-b border-n-weak">
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('TOUR_REPORTS.TABLE.AGENT') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('TOUR_REPORTS.TABLE.STATUS') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('TOUR_REPORTS.TABLE.SKIPPED_AT') }}
            </th>
            <th class="px-4 py-3" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="row in stats"
            :key="row.id"
            class="bg-n-background hover:bg-n-alpha-1 transition-colors"
          >
            <td class="px-4 py-3">
              <p class="font-medium text-n-slate-12">{{ row.name }}</p>
              <p class="text-xs text-n-slate-10">{{ row.email }}</p>
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="statusClass(row.status)"
              >
                {{ statusLabel(row.status) }}
              </span>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{
                row.skipped_at_step
                  ? $t('TOUR_REPORTS.TABLE.STEP_N', {
                      step: row.skipped_at_step,
                    })
                  : '—'
              }}
            </td>
            <td class="px-4 py-3 text-right">
              <button
                v-if="row.status !== 'pending'"
                type="button"
                class="text-xs text-n-brand hover:underline disabled:opacity-50"
                :disabled="isResetting"
                @click="resetUser(row)"
              >
                {{ $t('TOUR_REPORTS.TABLE.RESET') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
